// AssemblyOrders.js — manage physical bundle assembly
//
// Run this SQL in Supabase before using this page:
//
// create table assembly_orders (
//   id uuid primary key default gen_random_uuid(),
//   brand_id uuid references brands(id) not null,
//   assembly_number text not null,
//   bundle_product_id uuid references products(id) not null,
//   quantity integer not null,
//   status text not null default 'planned',   -- planned | completed | cancelled
//   scheduled_date date,
//   completed_date date,
//   produce_at_location_id uuid references locations(id),
//   notes text,
//   created_by uuid references user_profiles(id),
//   created_at timestamptz default now()
// );
// alter table assembly_orders enable row level security;
// create policy "brand_access" on assembly_orders
//   using (brand_id in (select brand_id from user_brand_access where user_id = auth.uid()));

import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { Plus, X, AlertTriangle, Package, CheckCircle2, XCircle, Layers } from 'lucide-react'
import { format } from 'date-fns'
import ProductPicker from '../components/ProductPicker'

const STATUS_BADGE = { planned: 'info', completed: 'success', cancelled: 'neutral' }

export default function AssemblyOrders() {
  const { activeBrand, user } = useAuth()
  const [orders, setOrders] = useState([])
  const [products, setProducts] = useState([])
  const [locations, setLocations] = useState([])
  const [onHand, setOnHand] = useState([])
  const [bom, setBom] = useState([])
  const [loading, setLoading] = useState(true)
  const [tableError, setTableError] = useState(false)
  const [statusFilter, setStatusFilter] = useState('planned')

  // New order modal
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState({ assembly_number: '', bundle_product_id: '', quantity: '', scheduled_date: '', produce_at_location_id: '', notes: '' })
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')

  // Complete assembly modal
  const [completeModal, setCompleteModal] = useState(false)
  const [completingOrder, setCompletingOrder] = useState(null)
  const [completeLocationId, setCompleteLocationId] = useState('')
  const [completing, setCompleting] = useState(false)
  const [completeError, setCompleteError] = useState('')

  useEffect(() => { if (activeBrand) load() }, [activeBrand])

  async function load() {
    setLoading(true)
    const [
      { data: ordersData, error: ordersError },
      { data: prodsData },
      { data: locsData },
      { data: ohData },
      { data: bomData }
    ] = await Promise.all([
      supabase.from('assembly_orders')
        .select('*, bundle_product:bundle_product_id(id, sku, name), produce_at_location:produce_at_location_id(name)')
        .eq('brand_id', activeBrand.id)
        .order('created_at', { ascending: false }),
      supabase.from('products').select('id, sku, name, product_type').eq('brand_id', activeBrand.id).is('deleted_at', null).order('sku'),
      supabase.from('locations').select('*').eq('brand_id', activeBrand.id).eq('is_active', true).order('name'),
      supabase.from('inventory_on_hand').select('*').eq('brand_id', activeBrand.id),
      supabase.from('bill_of_materials').select('*').eq('brand_id', activeBrand.id)
    ])

    if (ordersError && (ordersError.code === '42P01' || ordersError.message?.includes('does not exist'))) {
      setTableError(true)
      setLoading(false)
      return
    }

    setOrders(ordersData || [])
    setProducts(prodsData || [])
    setLocations(locsData || [])
    setOnHand(ohData || [])
    setBom(bomData || [])
    setTableError(false)
    setLoading(false)
  }

  // ── Helpers ──
  function getTotalOnHand(productId) {
    return onHand.filter(r => r.product_id === productId).reduce((sum, r) => sum + Number(r.on_hand), 0)
  }

  function getOnHandAtLoc(productId, locationId) {
    const row = onHand.find(r => r.product_id === productId && r.location_id === locationId)
    return row ? Number(row.on_hand) : 0
  }

  function getBomLines(bundleProductId) {
    return bom.filter(b => b.parent_product_id === bundleProductId)
  }

  function getComponentAvailability(bundleProductId, qty) {
    return getBomLines(bundleProductId).map(line => {
      const component = products.find(p => p.id === line.component_product_id)
      const required = Number(line.quantity) * qty
      const available = getTotalOnHand(line.component_product_id)
      return { component, bomLine: line, required, available, shortfall: Math.max(0, required - available) }
    })
  }

  function nextAssemblyNumber() {
    const nums = orders.map(o => {
      const m = o.assembly_number.match(/(\d+)$/)
      return m ? parseInt(m[1]) : 0
    })
    const next = nums.length ? Math.max(...nums) + 1 : 1
    return `AO-${String(next).padStart(4, '0')}`
  }

  const physicalBundles = products.filter(p => p.product_type === 'physical_bundle')
  const warehouseLocs = locations.filter(l => ['1pl', '3pl'].includes(l.type))
  const selectedBomLines = form.bundle_product_id && form.quantity
    ? getComponentAvailability(form.bundle_product_id, parseInt(form.quantity) || 0)
    : []

  // ── Create order ──
  function openNew() {
    setForm({ assembly_number: nextAssemblyNumber(), bundle_product_id: '', quantity: '', scheduled_date: '', produce_at_location_id: '', notes: '' })
    setFormError('')
    setModal(true)
  }

  async function saveOrder() {
    setSaving(true)
    setFormError('')
    if (!form.assembly_number || !form.bundle_product_id || !form.quantity) {
      setFormError('Assembly #, bundle, and quantity are required.')
      setSaving(false)
      return
    }
    const { error } = await supabase.from('assembly_orders').insert({
      brand_id: activeBrand.id,
      assembly_number: form.assembly_number,
      bundle_product_id: form.bundle_product_id,
      quantity: parseInt(form.quantity),
      scheduled_date: form.scheduled_date || null,
      produce_at_location_id: form.produce_at_location_id || null,
      notes: form.notes || null,
      status: 'planned',
      created_by: user?.id
    })
    if (error) { setFormError('Save failed: ' + error.message); setSaving(false); return }
    setSaving(false)
    setModal(false)
    load()
  }

  // ── Cancel order ──
  async function cancelOrder(order) {
    if (!window.confirm(`Cancel assembly order ${order.assembly_number}?`)) return
    await supabase.from('assembly_orders').update({ status: 'cancelled' }).eq('id', order.id)
    load()
  }

  // ── Complete assembly ──
  function openComplete(order) {
    setCompletingOrder(order)
    setCompleteLocationId(order.produce_at_location_id || '')
    setCompleteError('')
    setCompleteModal(true)
  }

  async function completeAssembly() {
    if (!completeLocationId) { setCompleteError('Select a location to add completed units to.'); return }
    setCompleting(true)
    setCompleteError('')

    const order = completingOrder
    const qty = order.quantity
    const availability = getComponentAvailability(order.bundle_product_id, qty)

    // Reject if any component is short
    const shortfalls = availability.filter(a => a.shortfall > 0)
    if (shortfalls.length > 0) {
      const msg = shortfalls.map(s => `${s.component?.sku || '?'}: need ${s.required}, have ${s.available}`).join(' | ')
      setCompleteError(`Insufficient components: ${msg}`)
      setCompleting(false)
      return
    }

    // Consume components from warehouse locations (greedy, largest first)
    const consumptionTxns = []
    for (const avail of availability) {
      let remaining = avail.required
      for (const loc of warehouseLocs) {
        if (remaining <= 0) break
        const locQty = getOnHandAtLoc(avail.bomLine.component_product_id, loc.id)
        if (locQty <= 0) continue
        const consume = Math.min(remaining, locQty)
        consumptionTxns.push({
          brand_id: activeBrand.id,
          product_id: avail.bomLine.component_product_id,
          location_id: loc.id,
          transaction_type: 'assembly_consumed',
          quantity: -consume,
          notes: `Assembly ${order.assembly_number}: ${qty}x ${order.bundle_product?.name}`,
          reference_id: order.id,
          reference_type: 'assembly_order',
          created_by: user?.id,
          source: 'manual'
        })
        remaining -= consume
      }
    }

    if (consumptionTxns.length > 0) {
      const { error: consumeErr } = await supabase.from('inventory_transactions').insert(consumptionTxns)
      if (consumeErr) { setCompleteError('Failed to record component consumption: ' + consumeErr.message); setCompleting(false); return }
    }

    // Add assembled bundle units
    const { error: bundleErr } = await supabase.from('inventory_transactions').insert({
      brand_id: activeBrand.id,
      product_id: order.bundle_product_id,
      location_id: completeLocationId,
      transaction_type: 'assembled',
      quantity: qty,
      notes: `Assembly ${order.assembly_number}: ${qty} units produced`,
      reference_id: order.id,
      reference_type: 'assembly_order',
      created_by: user?.id,
      source: 'manual'
    })
    if (bundleErr) { setCompleteError('Failed to record assembled units: ' + bundleErr.message); setCompleting(false); return }

    // Mark complete
    await supabase.from('assembly_orders').update({
      status: 'completed',
      completed_date: new Date().toISOString().split('T')[0],
      produce_at_location_id: completeLocationId
    }).eq('id', order.id)

    setCompleting(false)
    setCompleteModal(false)
    setCompletingOrder(null)
    load()
  }

  // ── Render ──
  const filteredOrders = statusFilter === 'all' ? orders : orders.filter(o => o.status === statusFilter)

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>

  return (
    <div className="page">
      <div className="page-header">
        <h1>Assembly Orders</h1>
        <div className="page-header-actions">
          {!tableError && (
            <button className="btn btn-primary" onClick={openNew}>
              <Plus size={14} />New Assembly Order
            </button>
          )}
        </div>
      </div>

      {/* ── Setup required ── */}
      {tableError && (
        <div className="card" style={{ borderColor: 'var(--warning)' }}>
          <div style={{ padding: '4px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <AlertTriangle size={16} style={{ color: 'var(--warning)' }} />
              <strong style={{ fontSize: '14px' }}>One-time setup required</strong>
            </div>
            <p style={{ fontSize: '13px', color: 'var(--text-2)', marginBottom: '12px' }}>
              Run this SQL in your <strong>Supabase SQL editor</strong> to create the <code>assembly_orders</code> table, then refresh:
            </p>
            <pre style={{ background: 'var(--bg)', border: '1px solid var(--border)', padding: '14px', borderRadius: 'var(--radius)', fontSize: '12px', lineHeight: 1.7, overflow: 'auto', margin: 0 }}>{`create table assembly_orders (
  id uuid primary key default gen_random_uuid(),
  brand_id uuid references brands(id) not null,
  assembly_number text not null,
  bundle_product_id uuid references products(id) not null,
  quantity integer not null,
  status text not null default 'planned',
  scheduled_date date,
  completed_date date,
  produce_at_location_id uuid references locations(id),
  notes text,
  created_by uuid references user_profiles(id),
  created_at timestamptz default now()
);

alter table assembly_orders enable row level security;

create policy "brand_access" on assembly_orders
  using (brand_id in (
    select brand_id from user_brand_access where user_id = auth.uid()
  ));`}</pre>
          </div>
        </div>
      )}

      {/* ── Main content ── */}
      {!tableError && (
        <>
          {/* Status filter tabs */}
          <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid var(--border)', marginBottom: '4px' }}>
            {[['planned', 'Planned'], ['completed', 'Completed'], ['cancelled', 'Cancelled'], ['all', 'All']].map(([val, label]) => (
              <button
                key={val}
                onClick={() => setStatusFilter(val)}
                style={{ padding: '8px 16px', background: 'none', border: 'none', borderBottom: `2px solid ${statusFilter === val ? 'var(--accent)' : 'transparent'}`, color: statusFilter === val ? 'var(--accent)' : 'var(--text-3)', fontFamily: 'var(--font)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', marginBottom: '-1px' }}
              >
                {label}
                {val === 'planned' && orders.filter(o => o.status === 'planned').length > 0 && (
                  <span style={{ marginLeft: '6px', background: 'var(--info)', color: 'white', borderRadius: '10px', padding: '1px 6px', fontSize: '10px', fontWeight: 700 }}>
                    {orders.filter(o => o.status === 'planned').length}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="card">
            {filteredOrders.length === 0 ? (
              <div className="empty-state">
                <Layers size={40} />
                <p>{statusFilter === 'planned' ? 'No planned assembly orders' : 'No orders found'}</p>
                {statusFilter === 'planned' && physicalBundles.length === 0 && (
                  <p style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '4px' }}>Add products with type "Physical Bundle" to the catalog first.</p>
                )}
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Assembly #</th>
                      <th>Bundle</th>
                      <th>Qty</th>
                      <th>Status</th>
                      <th>Scheduled</th>
                      <th>Completed</th>
                      <th>Produce At</th>
                      <th>Notes</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map(o => (
                      <tr key={o.id}>
                        <td><span className="mono">{o.assembly_number}</span></td>
                        <td style={{ fontWeight: 500 }}>
                          <span className="mono" style={{ fontSize: '11px', color: 'var(--text-3)', marginRight: '6px' }}>{o.bundle_product?.sku}</span>
                          {o.bundle_product?.name}
                        </td>
                        <td style={{ fontWeight: 600 }}>{o.quantity}</td>
                        <td><span className={`badge badge-${STATUS_BADGE[o.status] || 'neutral'}`} style={{ fontSize: '10px' }}>{o.status}</span></td>
                        <td style={{ fontSize: '12px', color: 'var(--text-2)' }}>{o.scheduled_date ? format(new Date(o.scheduled_date + 'T12:00:00'), 'MMM d, yyyy') : '—'}</td>
                        <td style={{ fontSize: '12px', color: 'var(--text-2)' }}>{o.completed_date ? format(new Date(o.completed_date + 'T12:00:00'), 'MMM d, yyyy') : '—'}</td>
                        <td style={{ fontSize: '12px', color: 'var(--text-2)' }}>{o.produce_at_location?.name || '—'}</td>
                        <td style={{ fontSize: '12px', color: 'var(--text-3)' }}>{o.notes || '—'}</td>
                        <td>
                          {o.status === 'planned' && (
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button className="btn btn-primary btn-sm" onClick={() => openComplete(o)}>
                                <CheckCircle2 size={13} />Complete
                              </button>
                              <button className="btn btn-ghost btn-sm" onClick={() => cancelOrder(o)} title="Cancel">
                                <XCircle size={13} style={{ color: 'var(--danger)' }} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── New Assembly Order Modal ── */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <h2>New Assembly Order</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              {formError && <div className="alert alert-danger">{formError}</div>}

              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Assembly # *</label>
                  <input className="input" value={form.assembly_number} onChange={e => setForm(f => ({ ...f, assembly_number: e.target.value }))} placeholder="AO-0001" />
                </div>
                <div className="form-group">
                  <label className="form-label">Scheduled Date</label>
                  <input className="input" type="date" value={form.scheduled_date} onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))} />
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Bundle Product *</label>
                  <ProductPicker
                    products={physicalBundles}
                    value={form.bundle_product_id}
                    onChange={id => setForm(f => ({ ...f, bundle_product_id: id }))}
                    placeholder="Search for a physical bundle..."
                  />
                  {physicalBundles.length === 0 && (
                    <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '4px' }}>No physical bundles in catalog yet. Add products with type "Physical Bundle."</div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Quantity *</label>
                  <input className="input" type="number" min="1" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} placeholder="100" />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Produce At Location</label>
                <select className="select" value={form.produce_at_location_id} onChange={e => setForm(f => ({ ...f, produce_at_location_id: e.target.value }))}>
                  <option value="">— Select warehouse location —</option>
                  {warehouseLocs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>

              {/* BOM preview */}
              {form.bundle_product_id && (
                <div style={{ background: 'var(--bg-3)', borderRadius: 'var(--radius)', padding: '14px 16px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
                    Components Required
                  </div>
                  {getBomLines(form.bundle_product_id).length === 0 ? (
                    <div style={{ fontSize: '13px', color: 'var(--warning)' }}>
                      ⚠ No bill of materials defined for this bundle. Add components in Catalog → Edit Product.
                    </div>
                  ) : (
                    <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ fontSize: '11px', color: 'var(--text-3)' }}>
                          <th style={{ textAlign: 'left', paddingBottom: '6px', fontWeight: 500 }}>Component</th>
                          <th style={{ textAlign: 'right', paddingBottom: '6px', fontWeight: 500 }}>Per Bundle</th>
                          <th style={{ textAlign: 'right', paddingBottom: '6px', fontWeight: 500 }}>Total Needed</th>
                          <th style={{ textAlign: 'right', paddingBottom: '6px', fontWeight: 500 }}>On Hand</th>
                          <th style={{ textAlign: 'right', paddingBottom: '6px', fontWeight: 500 }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedBomLines.map((avail, i) => (
                          <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                            <td style={{ padding: '6px 0' }}>
                              <span className="mono" style={{ fontSize: '11px', color: 'var(--text-3)' }}>{avail.component?.sku}</span>
                              <span style={{ color: 'var(--text-2)', marginLeft: '8px' }}>{avail.component?.name || '—'}</span>
                            </td>
                            <td style={{ textAlign: 'right', color: 'var(--text-3)' }}>{avail.bomLine.quantity}×</td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>{avail.required}</td>
                            <td style={{ textAlign: 'right' }}>{avail.available}</td>
                            <td style={{ textAlign: 'right' }}>
                              {avail.shortfall > 0
                                ? <span className="badge badge-danger" style={{ fontSize: '10px' }}>Short {avail.shortfall}</span>
                                : <span className="badge badge-success" style={{ fontSize: '10px' }}>✓ OK</span>
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="textarea" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveOrder} disabled={saving || !form.assembly_number || !form.bundle_product_id || !form.quantity}>
                {saving ? 'Saving...' : 'Create Assembly Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Complete Assembly Modal ── */}
      {completeModal && completingOrder && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setCompleteModal(false)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <h2>Complete Assembly — {completingOrder.assembly_number}</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setCompleteModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              {completeError && <div className="alert alert-danger">{completeError}</div>}

              <div style={{ padding: '12px 14px', background: 'var(--accent-bg)', borderRadius: 'var(--radius)', marginBottom: '16px', fontSize: '13px', color: 'var(--text-2)' }}>
                Assembling <strong style={{ color: 'var(--text)' }}>{completingOrder.quantity} units</strong> of{' '}
                <strong style={{ color: 'var(--text)' }}>{completingOrder.bundle_product?.name}</strong>. Components will be deducted and assembled units added to the selected location.
              </div>

              <div className="form-group">
                <label className="form-label">Add Completed Units To *</label>
                <select className="select" value={completeLocationId} onChange={e => setCompleteLocationId(e.target.value)}>
                  <option value="">— Select warehouse location —</option>
                  {warehouseLocs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>

              {/* Component consumption preview */}
              <div style={{ background: 'var(--bg-3)', borderRadius: 'var(--radius)', padding: '14px 16px' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
                  Components to Consume
                </div>
                {(() => {
                  const avails = getComponentAvailability(completingOrder.bundle_product_id, completingOrder.quantity)
                  if (avails.length === 0) return <div style={{ fontSize: '13px', color: 'var(--warning)' }}>⚠ No BOM defined — cannot complete assembly.</div>
                  return (
                    <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ fontSize: '11px', color: 'var(--text-3)' }}>
                          <th style={{ textAlign: 'left', paddingBottom: '6px', fontWeight: 500 }}>Component</th>
                          <th style={{ textAlign: 'right', paddingBottom: '6px', fontWeight: 500 }}>Consume</th>
                          <th style={{ textAlign: 'right', paddingBottom: '6px', fontWeight: 500 }}>Available</th>
                          <th style={{ textAlign: 'right', paddingBottom: '6px', fontWeight: 500 }}>Remaining After</th>
                          <th style={{ textAlign: 'right', paddingBottom: '6px', fontWeight: 500 }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {avails.map((avail, i) => (
                          <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                            <td style={{ padding: '6px 0' }}>
                              <span className="mono" style={{ fontSize: '11px', color: 'var(--text-3)' }}>{avail.component?.sku}</span>
                              <span style={{ color: 'var(--text-2)', marginLeft: '8px' }}>{avail.component?.name || '—'}</span>
                            </td>
                            <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--danger)' }}>−{avail.required}</td>
                            <td style={{ textAlign: 'right' }}>{avail.available}</td>
                            <td style={{ textAlign: 'right', fontWeight: 600, color: avail.shortfall > 0 ? 'var(--danger)' : 'var(--success)' }}>
                              {avail.available - avail.required}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              {avail.shortfall > 0
                                ? <span className="badge badge-danger" style={{ fontSize: '10px' }}>Short {avail.shortfall}</span>
                                : <span className="badge badge-success" style={{ fontSize: '10px' }}>✓ OK</span>
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                })()}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setCompleteModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={completeAssembly} disabled={completing || !completeLocationId}>
                {completing ? 'Completing...' : `Complete — Add ${completingOrder.quantity} units to inventory`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
